import axios from 'axios';
import { GetZaloTemplateDetailResponse, GetZaloTemplateListParams, GetZaloTemplateListResponse, Message, ZaloTemplateCreateRequest, ZaloTemplateCreateResponse, ZBSTemplateMessageRequest, ZBSTemplateMessageResponse } from '../types/message';
import { configService } from './configService';
import { db } from '../config/firebase';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    orderBy
} from 'firebase/firestore';

const COLLECTION_NAME = 'messages';
const ZALO_PROXY_URL = process.env.REACT_APP_ZALO_PROXY_URL;
const BANNER_MAX_SIZE = '1MB';

const removeUndefinedFields = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map((item) => removeUndefinedFields(item)) as T;
    }

    if (value && typeof value === 'object') {
        if (value instanceof Date || value instanceof File) {
            return value;
        }

        return Object.entries(value as Record<string, unknown>).reduce((acc, [key, entry]) => {
            if (entry !== undefined) {
                acc[key] = removeUndefinedFields(entry);
            }
            return acc;
        }, {} as Record<string, unknown>) as T;
    }

    return value;
};

const resolveZaloProxyUrl = () => {
    const proxyUrl = (ZALO_PROXY_URL || '').trim();
    if (!proxyUrl) {
        throw new Error('Thiếu REACT_APP_ZALO_PROXY_URL. Vui lòng cấu hình FE gọi Zalo qua backend proxy.');
    }

    return proxyUrl;
};

const buildZaloProxyAuthHeaders = async (contentType: string) => {
    const config = await configService.getConfig();
    const accessToken = (config?.access_token_zalo || '').trim();
    if (!accessToken) {
        throw new Error('Thiếu access_token_zalo trong cấu hình hệ thống');
    }

    const headers: Record<string, string> = {
        'Content-Type': contentType
    };
    headers['access-token'] = accessToken;
    headers['x-zalo-access-token'] = accessToken;
    headers.Authorization = `Bearer ${accessToken}`;

    return headers;
};

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

interface ZaloMessageElement {
    title?: string;
    image_url?: string;
    subtitle?: string;
    default_action?: {
        type: string;
        url?: string;
    };
}

interface ZaloMessageButton {
    title: string;
    type: string;
    payload?: any;
}

interface ZaloMessagePayload {
    template_type: string;
    elements: ZaloMessageElement[];
    buttons?: ZaloMessageButton[];
}

interface ZaloMessage {
    recipient: {
        user_id: string;
    };
    message: {
        attachment: {
            type: string;
            payload: ZaloMessagePayload;
        };
    };
}

export const messageService = {
    getAllMessages: async () => {
        try {
            const querySnapshot = await getDocs(
                query(collection(db, COLLECTION_NAME), orderBy('timestamp', 'desc'))
            );
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Message[];
        } catch (error) {
            throw new Error('Could not fetch messages: ' + error);
        }
    },

    getMessage: async (id: string) => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                throw new Error('Message not found');
            }
            return { id: docSnap.id, ...docSnap.data() } as Message;
        } catch (error) {
            throw new Error('Could not fetch message: ' + error);
        }
    },

    uploadBanner: async (file: File): Promise<string> => {
        try {
            const storageRef = ref(storage, `message-banners/${Date.now()}-${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(snapshot.ref);
            return downloadUrl;
        } catch (error) {
            throw new Error('Could not upload banner: ' + error);
        }
    },

    createMessage: async (message: Partial<Message>, bannerFile?: File) => {
        try {
            let bannerUrl = '';
            if (bannerFile) {
                bannerUrl = await messageService.uploadBanner(bannerFile);
            }

            const messageData = {
                ...message,
                template_data: {
                    ...message.template_data,
                    banner: bannerUrl ? {
                        image_url: bannerUrl,
                        max_size: BANNER_MAX_SIZE,
                        ratio: "1:1.5"
                    } : undefined
                },
                // Đảm bảo related_products được lưu đúng cách
                related_products: message.related_products || []
            };
            const firestoreMessageData = removeUndefinedFields(messageData);

            // Save to Firebase first
            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...firestoreMessageData,
                timestamp: new Date(),
                status: 'sent'
            });

            const savedMessage = { id: docRef.id, ...firestoreMessageData } as Message;

            // If it's a Zalo template message, send it through Zalo API
            if (message.type === 'template' && message.template_data) {
                // const config = await configService.getConfig();
                
                // Convert our template to Zalo format
                const zaloMessage: ZaloMessage = {
                    recipient: {
                        user_id: message.receiverId || ''
                    },
                    message: {
                        attachment: {
                            type: "template",
                            payload: {
                                template_type: "list",
                                elements: []
                            }
                        }
                    }
                };

                // Add banner if exists
                if (message.template_data.banner) {
                    zaloMessage.message.attachment.payload.elements.push({
                        image_url: message.template_data.banner.image_url
                    });
                }

                // Add header if exists
                if (message.template_data.header) {
                    zaloMessage.message.attachment.payload.elements.push({
                        title: message.template_data.header.content
                    });
                }

                // Add table data if exists
                if (message.template_data.table?.rows) {
                    message.template_data.table.rows.forEach(row => {
                        zaloMessage.message.attachment.payload.elements.push({
                            title: `${row.key}: ${row.value}`
                        });
                    });
                }

                // Add text if exists
                if (message.template_data.text) {
                    zaloMessage.message.attachment.payload.elements.push({
                        title: message.template_data.text.content
                    });
                }

                // Add buttons if exists
                if (message.template_data.buttons) {
                    zaloMessage.message.attachment.payload.buttons = message.template_data.buttons.map(button => ({
                        title: button.title,
                        type: button.type || "oa.open.phone",
                        payload: button.payload
                    }));
                }

                // // Send to Zalo
                // await axios.post('https://openapi.zalo.me/v3.0/oa/message/cs', zaloMessage, {
                //     headers: {
                //         'access_token': config?.access_token_zalo,
                //         'Content-Type': 'application/json'
                //     }
                // });
            }

            return savedMessage;
        } catch (error) {
            throw new Error('Could not create message: ' + error);
        }
    },

    updateMessage: async (id: string, message: Partial<Message>, bannerFile?: File) => {
        try {
            let bannerUrl = message.template_data?.banner?.image_url;
            
            if (bannerFile) {
                bannerUrl = await messageService.uploadBanner(bannerFile);
            }

            const messageData = {
                ...message,
                template_data: {
                    ...message.template_data,
                    banner: bannerUrl ? {
                        image_url: bannerUrl,
                        max_size: BANNER_MAX_SIZE,
                        ratio: "1:1.5"
                    } : undefined
                },
                // Cập nhật related_products
                related_products: message.related_products || []
            };
            const firestoreMessageData = removeUndefinedFields(messageData);

            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, {
                ...firestoreMessageData,
                updatedAt: new Date()
            });
            return { id, ...firestoreMessageData };
        } catch (error) {
            throw new Error('Could not update message: ' + error);
        }
    },

    deleteMessage: async (id: string) => {
        try {
            // Get message data first to delete banner if exists
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const bannerUrl = data.template_data?.banner?.image_url;
                
                // Xóa banner từ Firebase Storage nếu tồn tại
                if (bannerUrl && isFirebaseStorageUrl(bannerUrl)) {
                    try {
                        const bannerRef = ref(storage, bannerUrl);
                        await deleteObject(bannerRef);
                        console.log(`Successfully deleted message banner: ${bannerUrl}`);
                    } catch (error) {
                        console.error(`Error deleting message banner ${bannerUrl}:`, error);
                        // Tiếp tục xử lý dù có lỗi khi xóa ảnh
                    }
                } else if (bannerUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${bannerUrl}`);
                }

                console.log(`Storage cleanup for message ID ${id}: ${bannerUrl ? (isFirebaseStorageUrl(bannerUrl) ? '1 banner deleted' : '1 non-Firebase URL skipped') : 'no banner to delete'}`);
            }

            // Xóa document từ Firestore
            await deleteDoc(docRef);
            console.log(`Successfully deleted message document with ID: ${id}`);
            return true;
        } catch (error) {
            console.error(`Error deleting message with ID ${id}:`, error);
            throw new Error('Could not delete message: ' + error);
        }
    },

    markAsRead: async (id: string) => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, {
                status: 'read',
                updatedAt: new Date()
            });
            return true;
        } catch (error) {
            throw new Error('Could not mark message as read: ' + error);
        }
    },

    getZaloTemplateList: async (params: Partial<GetZaloTemplateListParams> = {}): Promise<GetZaloTemplateListResponse> => {
        try {
            const config = await configService.getConfig();
            if (!config?.access_token_zalo) {
                throw new Error('Thiếu access_token_zalo trong cấu hình hệ thống');
            }

            const {
                offset = 0,
                limit = 100,
                status,
                filterPreset = 1
            } = params;

            const response = await axios.get<GetZaloTemplateListResponse>(
                'https://business.openapi.zalo.me/template/all',
                {
                    params: {
                        offset,
                        limit: Math.min(limit, 100),
                        ...(status ? { status } : {}),
                        ...(filterPreset !== undefined ? { filterPreset } : {})
                    },
                    headers: {
                        'access_token': config.access_token_zalo,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.error !== 0) {
                throw new Error(response.data.message || 'Không lấy được danh sách template từ Zalo');
            }

            return response.data;
        } catch (error: any) {
            throw new Error('Could not fetch Zalo template list: ' + (error?.message || error));
        }
    },

    getZaloTemplateDetail: async (templateId: string): Promise<GetZaloTemplateDetailResponse> => {
        try {
            const config = await configService.getConfig();
            if (!config?.access_token_zalo) {
                throw new Error('Thiếu access_token_zalo trong cấu hình hệ thống');
            }

            const response = await axios.get<GetZaloTemplateDetailResponse>(
                'https://business.openapi.zalo.me/template/info/v2',
                {
                    params: {
                        template_id: templateId
                    },
                    headers: {
                        'access_token': config.access_token_zalo,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.error !== 0) {
                throw new Error(response.data.message || 'Không lấy được chi tiết template từ Zalo');
            }

            return response.data;
        } catch (error: any) {
            throw new Error('Could not fetch Zalo template detail: ' + (error?.message || error));
        }
    },

    createZaloTemplate: async (payload: ZaloTemplateCreateRequest): Promise<ZaloTemplateCreateResponse> => {
        try {
            const proxyUrl = resolveZaloProxyUrl();

            const response = await axios.post<ZaloTemplateCreateResponse>(
                `${proxyUrl}/template/create`,
                payload,
                {
                    headers: await buildZaloProxyAuthHeaders('application/json')
                }
            );

            if (response.data.error !== 0) {
                throw new Error(response.data.message || 'Không tạo được template mới trên Zalo');
            }

            return response.data;
        } catch (error: any) {
            const backendMessage = error?.response?.data?.message;
            if (backendMessage) {
                throw new Error(backendMessage);
            }
            throw new Error('Could not create Zalo template: ' + (error?.message || error));
        }
    },

    uploadZaloTemplateImage: async (file: File): Promise<string> => {
        try {
            const proxyUrl = resolveZaloProxyUrl();

            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(
                `${proxyUrl}/template/upload-image`,
                formData,
                {
                    headers: await buildZaloProxyAuthHeaders('multipart/form-data')
                }
            );

            if (response.data?.error && response.data.error !== 0) {
                throw new Error(response.data?.message || 'Upload ảnh lên Zalo thất bại');
            }

            const mediaId =
                response.data?.data?.media_id ||
                response.data?.data?.attachment_id ||
                response.data?.media_id ||
                response.data?.attachment_id;

            if (!mediaId) {
                throw new Error('Không nhận được media_id/attachment_id từ Zalo');
            }

            return String(mediaId);
        } catch (error: any) {
            const backendMessage = error?.response?.data?.message;
            if (backendMessage) {
                throw new Error(backendMessage);
            }
            throw new Error('Could not upload Zalo image: ' + (error?.message || error));
        }
    },

    checkZaloTemplatePermission: async (): Promise<any> => {
        try {
            const proxyUrl = resolveZaloProxyUrl();

            const response = await axios.get(
                `${proxyUrl}/template/check-permission`,
                {
                    params: {
                        offset: 0,
                        limit: 1,
                        filterPreset: 1
                    },
                    headers: await buildZaloProxyAuthHeaders('application/json')
                }
            );

            if (response.data?.error && response.data.error !== 0) {
                throw new Error(response.data?.message || 'OA chưa có quyền sử dụng template resource');
            }

            return response.data;
        } catch (error: any) {
            const backendMessage = error?.response?.data?.message;
            if (backendMessage) {
                throw new Error(backendMessage);
            }
            throw new Error('Could not check Zalo template permission: ' + (error?.message || error));
        }
    },

    sendZBSTemplateMessage: async (payload: ZBSTemplateMessageRequest): Promise<ZBSTemplateMessageResponse> => {
        try {
            const proxyUrl = resolveZaloProxyUrl();

            const response = await axios.post<ZBSTemplateMessageResponse>(
                `${proxyUrl}/message/template`,
                payload,
                {
                    headers: await buildZaloProxyAuthHeaders('application/json'),
                    timeout: 30000
                }
            );

            if (response.data.error !== 0) {
                throw new Error(response.data.message || 'Không gửi được ZBS template message');
            }

            return response.data;
        } catch (error: any) {
            const backendMessage = error?.response?.data?.message;
            if (backendMessage) {
                throw new Error(backendMessage);
            }
            throw new Error('Could not send ZBS template message: ' + (error?.message || error));
        }
    },

    sendZBSTemplateMessageDevelopment: async (payload: ZBSTemplateMessageRequest): Promise<ZBSTemplateMessageResponse> => {
        try {
            const proxyUrl = resolveZaloProxyUrl();

            const response = await axios.post<ZBSTemplateMessageResponse>(
                `${proxyUrl}/message/template/development`,
                payload,
                {
                    headers: await buildZaloProxyAuthHeaders('application/json'),
                    timeout: 30000
                }
            );

            if (response.data.error !== 0) {
                throw new Error(response.data.message || 'Không gửi được template test (development mode)');
            }

            return response.data;
        } catch (error: any) {
            const backendMessage = error?.response?.data?.message;
            if (backendMessage) {
                throw new Error(backendMessage);
            }
            throw new Error('Could not send ZBS template message (development): ' + (error?.message || error));
        }
    },

    sendZaloMessage: async (messageData: any) => {
        try {
            const config = await configService.getConfig();
            const response = await fetch('https://openapi.zalo.me/v3.0/oa/message/promotion', {
                method: 'POST',
                headers: {
                    'access_token': config?.access_token_zalo || '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(messageData)
            });

            if (!response.ok) {
                throw new Error('Failed to send Zalo message');
            }

            return await response.json();
        } catch (error) {
            console.error('Error sending Zalo message:', error);
            throw error;
        }
    }
}; 
